import type { IncomingInvoiceFormData } from "@kundeninfo/api-client";
import { formatDezimalBetrag } from "../../format.js";
import { periodText } from "../suppliers/shared.js";

/**
 * Was NUR die Eingangsrechnungen im Web brauchen (Plan Aufgabe 17, Fix-Runde 1 K8): Schemagrenzen, Steuer zum Netto,
 * Posten. Betrags- und Centform, der heutige Tag, Feldfehler, Teilzeitraum und die Fragen stehen für Lieferverträge
 * und Eingangsrechnungen gemeinsam in `suppliers/shared.tsx`, die Kontierungslisten bei ihren Feldern
 * (`sharedAssignmentLists`, components/KontierungFelder.tsx) — Aufgabe 22.
 */
type Charge = IncomingInvoiceFormData["charges"][number];

/** Schemagrenzen der API (Aufgabe 16): Rechnungsnummer 100 Zeichen, Stornogrund 500, höchstens 100 Betragszeilen (die
 *  Länge eines Betragsfelds ist `AMOUNT_FIELD_MAX` in suppliers/shared.tsx). */
export const NUMBER_MAX = 100;
export const CANCEL_REASON_MAX = 500;
export const LINES_MAX = 100;

/** Beträge der Erfassung tragen ein Vorzeichen (Gutschrift, Guthaben) — für `amountForApi`. */
export const SIGNED = { signed: true } as const;

/** Steuer zum Netto, kaufmännisch auf Cent (der Satz mit höchstens drei Nachkommastellen) — wie `suggestedTax` der Domäne. */
export function taxFor(net: bigint, ratePercent: number): bigint {
  const n = net * BigInt(Math.round(ratePercent * 1000));
  const q = n / 100000n;
  const rest = n % 100000n;
  return (rest < 0n ? -rest : rest) * 2n >= 100000n ? q + (n < 0n ? -1n : 1n) : q;
}

/** Das Kästchen eines Postens: „Jan. 2026 · 59,38 €“. */
export const chargeText = (c: Pick<Charge, "subperiodStart" | "subperiodEnd" | "grossAmount">, currency = "EUR"): string =>
  `${periodText(c.subperiodStart, c.subperiodEnd)} · ${formatDezimalBetrag(c.grossAmount, currency)}`;

/** Offen (ungebunden, geplant, ohne Zahlung — wie `suggestCharges` der Domäne) oder an DIESER Rechnung gebunden. */
export function offeredCharges(charges: readonly Charge[], invoiceId: string | null): Charge[] {
  return charges.filter((c) => (c.incomingInvoiceId === null
    ? c.status === "PLANNED" && Number(c.paidAmount ?? "0") === 0 : c.incomingInvoiceId === invoiceId));
}

/** Die Kästchen, die ohne „weitere“ stehen (A-S1): vorgeschlagene, gespeicherte (`saved`) und die bis `until` fälligen —
 *  in der Ordnung des Plans. */
export function shownCharges(offered: readonly Charge[], saved: readonly string[], until: string): Charge[] {
  return offered.filter((c) => c.suggested || saved.includes(c.id) || c.dueDate <= until);
}
